import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MapPin, Phone } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { useSeo } from "@/lib/useSeo";
import { COMPANY } from "@/data/company";

export default function Realisations() {
  useSeo("Nos réalisations", "Découvrez les chantiers de maçonnerie, béton, murs et aménagements extérieurs réalisés par SMT Provence en Provence.");
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  useEffect(() => { api.get("/realisations").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const cats = [...new Set(items.map((i) => i.category))];
  const filtered = cat ? items.filter((i) => i.category === cat) : items;
  const featured = cat === "" ? filtered[0] : null;
  const rest = featured ? filtered.slice(1) : filtered;

  return (
    <div>
      {/* Header */}
      <section className="pt-32 pb-12 bg-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white">Réalisations</h1>
            <p className="mt-4 text-white/70 max-w-xl">Chaque projet est une étude de cas : le contexte, notre solution et le résultat livré.</p>
          </div>
          <div className="flex gap-8 shrink-0">
            <div><p className="font-heading text-4xl font-black text-primary">{items.length}</p><p className="text-xs text-white/50 uppercase tracking-wide">Réalisations présentées</p></div>
            <div><p className="font-heading text-4xl font-black text-primary">{cats.length}</p><p className="text-xs text-white/50 uppercase tracking-wide">Domaines</p></div>
          </div>
        </div>
      </section>

      {/* Filters (sticky) */}
      <div className="sticky top-[72px] z-30 bg-background/90 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap gap-2">
          <button onClick={() => setCat("")} data-testid="filter-all" className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${cat === "" ? "bg-primary text-white border-primary" : "bg-white text-ink border-border hover:border-primary"}`}>Toutes</button>
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${cat === c ? "bg-primary text-white border-primary" : "bg-white text-ink border-border hover:border-primary"}`}>{c}</button>
          ))}
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Featured */}
        {featured && (
          <Reveal>
            <Link to={`/realisations/${featured.slug}`} data-testid={`realisation-${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-14">
              <div className="overflow-hidden rounded-3xl aspect-[16/11]">
                <img src={fileUrl(featured.cover_image)} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div>
                <h2 className="font-heading text-3xl sm:text-4xl font-black text-ink group-hover:text-primary transition-colors">{featured.title}</h2>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink/50">
                  {featured.location && <span className="inline-flex items-center gap-1.5"><MapPin size={15} /> {featured.location}</span>}
                  <span className="text-primary font-semibold">{featured.category}</span>
                  {featured.year && <span className="text-ink/30">· {featured.year}</span>}
                </div>
                <p className="mt-4 text-ink/70 leading-relaxed line-clamp-3">{featured.summary}</p>
                <span className="mt-6 inline-flex items-center gap-2 bg-ink text-white px-6 py-3 rounded-full font-bold group-hover:bg-primary transition">Voir l'étude de cas <ArrowRight size={18} /></span>
              </div>
            </Link>
          </Reveal>
        )}

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((r, i) => (
            <Reveal key={r.id} delay={(i % 3) * 0.08}>
              <Link to={`/realisations/${r.slug}`} data-testid={`realisation-${r.slug}`} className="group block bg-card border border-border rounded-2xl overflow-hidden hover:border-primary hover:-translate-y-1 transition-all duration-300 h-full">
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img src={fileUrl(r.cover_image)} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-[11px] font-bold text-ink">{r.category}</div>
                  <div className="absolute top-3 right-3 h-9 w-9 rounded-full bg-primary text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"><ArrowUpRight size={18} /></div>
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-lg font-black text-ink group-hover:text-primary transition-colors line-clamp-2">{r.title}</h3>
                  <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-ink/50">{r.location && <><MapPin size={14} /> {r.location}</>}{r.year && <span className="text-ink/30">· {r.year}</span>}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        {filtered.length === 0 && <p className="text-ink/50 text-center py-16">Aucune réalisation pour le moment.</p>}
      </section>

      {/* CTA */}
      <section className="bg-secondary/40 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div><h3 className="font-heading text-2xl sm:text-3xl font-black text-ink">Un projet similaire en tête ?</h3><p className="text-ink/60 mt-1">Recevez un devis gratuit et sans engagement.</p></div>
          <div className="flex gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3.5 rounded-full font-bold hover:brightness-95 transition">Demander un devis <ArrowRight size={18} /></Link>
            <a href={`tel:${COMPANY.phoneHref}`} className="inline-flex items-center gap-2 bg-ink text-white px-6 py-3.5 rounded-full font-bold hover:bg-primary transition"><Phone size={18} /> Appeler</a>
          </div>
        </div>
      </section>
    </div>
  );
}
